<template lang="pug">
.mis-patologias
  .mp-head
    h1.mp-title Mis patologías
    p.mp-sub Marcá las condiciones de salud que tenés para que el Municipio pueda hacer seguimiento y prevención. Podés cambiarlo cuando quieras.

  .mp-notice(v-if="!enabled")
    p Esta sección no está disponible en tu portal.

  template(v-else)
    .mp-loading(v-if="loading")
      .mp-spinner
      span Cargando…

    template(v-else)
      .mp-empty(v-if="!catalog.length")
        p Todavía no hay patologías configuradas.

      .mp-list(v-else)
        label.mp-item(v-for="c in catalog" :key="c._id" :class="{ 'is-checked': isChecked(c._id) }")
          input.mp-item__box(type="checkbox" :value="c._id" :checked="isChecked(c._id)" @change="toggle(c._id)")
          span.mp-item__check
          span.mp-item__name {{ c.name }}

      .mp-footer
        p.mp-updated(v-if="updatedAtLabel") Última actualización: {{ updatedAtLabel }}
        button.mp-save(:disabled="saving || !catalog.length" @click="save")
          | {{ saving ? 'Guardando…' : 'Guardar' }}
</template>

<script>
import { format, parseISO } from "date-fns";
import {
  hasPatientConditionsCapability,
  selectedConditionIds,
  toggleConditionId,
  loadConditionsCatalog,
  saveMyConditions,
} from "@/services/patient-conditions";

/**
 * @component MisPatologias
 * @description UC-REG-314 — Portal del paciente (Campana): "Mis patologías".
 *   Checkboxes con el catálogo activo de patologías crónicas de la
 *   organización; el vecino marca las suyas para seguimiento y prevención.
 *   Visible solo si la API expone la capacidad `patientChronicConditions`
 *   (`GET /organization/me`). La misma vista la embebe la app móvil en un
 *   WebView (`/patient/mis-patologias`).
 * @example <MisPatologias />
 */
export default {
  name: "MisPatologias",
  data() {
    return {
      loading: true,
      saving: false,
      /** @type {Array<{_id:string,code:string,name:string}>} */
      catalog: [],
      /** @type {string[]} ids marcados */
      selected: [],
    };
  },
  computed: {
    /** @returns {boolean} capacidad del tenant (API decide) */
    enabled() {
      return hasPatientConditionsCapability(
        this.$store.state.organizationContext
      );
    },
    /** @returns {string} fecha de la última actualización (dd/MM/yyyy) */
    updatedAtLabel() {
      const user = this.$store.state.user;
      const raw =
        user &&
        user.patient &&
        user.patient.health &&
        user.patient.health.chronicConditionsUpdatedAt;
      if (!raw) return "";
      try {
        const date = typeof raw === "string" ? parseISO(raw) : raw;
        return format(date, "dd/MM/yyyy");
      } catch (e) {
        return "";
      }
    },
  },
  async created() {
    if (!this.enabled) {
      this.loading = false;
      return;
    }
    this.selected = selectedConditionIds(this.$store.state.user);
    try {
      this.catalog = await loadConditionsCatalog();
    } catch (e) {
      this.$message.error("No pudimos cargar las patologías.");
    } finally {
      this.loading = false;
    }
  },
  methods: {
    /**
     * @description ¿Está marcada la patología?
     * @param {string} id
     * @returns {boolean}
     */
    isChecked(id) {
      return this.selected.includes(String(id));
    },
    /**
     * @description Marca / desmarca una patología.
     * @param {string} id
     */
    toggle(id) {
      this.selected = toggleConditionId(this.selected, String(id));
    },
    /** @description Guarda la selección en la API. */
    async save() {
      this.saving = true;
      try {
        this.selected = await saveMyConditions(this.selected);
        this.$message.success("Guardamos tus patologías.");
      } catch (e) {
        this.$message.error("No pudimos guardar. Intentá de nuevo.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.mis-patologias {
  max-width: 640px;
  margin: 0 auto;
  padding: 24px 16px 48px;
}
.mp-title {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 700;
}
.mp-sub {
  margin: 0 0 24px;
  color: #6b7280;
  line-height: 1.5;
}
.mp-notice,
.mp-empty {
  padding: 16px;
  border-radius: 12px;
  background: #f3f4f6;
  color: #4b5563;
}
.mp-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #6b7280;
}
.mp-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: mp-spin 0.8s linear infinite;
}
@keyframes mp-spin {
  to {
    transform: rotate(360deg);
  }
}
.mp-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.mp-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  &.is-checked {
    border-color: #4f46e5;
    background: #eef2ff;
  }
}
.mp-item__box {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.mp-item__check {
  width: 22px;
  height: 22px;
  border: 2px solid #9ca3af;
  border-radius: 6px;
  flex: 0 0 22px;
  position: relative;
  .is-checked & {
    border-color: #4f46e5;
    background: #4f46e5;
    &::after {
      content: "";
      position: absolute;
      left: 6px;
      top: 2px;
      width: 6px;
      height: 11px;
      border: solid #fff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
}
.mp-item__name {
  font-size: 16px;
  font-weight: 500;
}
.mp-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 24px;
}
.mp-updated {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
}
.mp-save {
  margin-left: auto;
  padding: 12px 24px;
  border: 0;
  border-radius: 10px;
  background: #4f46e5;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}
</style>
